import { Directive, ElementRef, EventEmitter, HostListener, Injector, Input, OnDestroy, Output } from '@angular/core';
import { BehaviorSubject, combineLatest, Subscription, switchMap, withLatestFrom } from 'rxjs';
import { debounceTime, filter, take, takeWhile } from 'rxjs/operators';
import { PreviewOverlayService, Screen, ScreenService } from '../../services';
import { OsnComponentBase } from '../../utils';
import { AbstractPlayButtonService } from '../play-button/abstract-play-button.service';
import { PreviewOverlaySource, ShallowPreviewOverlayViewModel } from './preview-overlay.view-model';

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: '[osnSharedPreviewOverlay]',
})
export class PreviewOverlayDirective extends OsnComponentBase implements OnDestroy, PreviewOverlaySource {
  @Input() public overlayVm: ShallowPreviewOverlayViewModel;
  @Input() public playMediaOnClick = false;
  @Input() public preventHover = false;
  @Output() public hovered = new EventEmitter<boolean>();
  @Output() public itemSelect: EventEmitter<string> = new EventEmitter();
  @Input() public buttonText: string;
  @Input() public trayName: string;
  private readonly isMouseOverOnThumbnail$ = new BehaviorSubject<boolean>(false);
  private isPreviewOverlayOpen = false;
  private overlayVisibleSubscription?: Subscription;

  constructor(
    injector: Injector,
    private readonly previewOverlayService: PreviewOverlayService,
    private readonly elementRef: ElementRef,
    private readonly screenService: ScreenService,
    private readonly playService: AbstractPlayButtonService
  ) {
    super(injector);
    this.previewOverlayService.registerOverlaySource(this);
  }

  @HostListener('mouseenter', ['event'])
  public onMouseEnter() {
    if (!this.overlayVm || this.preventHover) {
      return;
    }

    this.isMouseOverOnThumbnail$.next(true);

    if (this.isPreviewOverlayOpen || this.hidePreviewOverlay()) {
      return;
    }

    this.overlayVisibleSubscription = this.previewOverlayService.windowScrolled$
      .pipe(
        take(1),
        withLatestFrom(this.isMouseOverOnThumbnail$),
        takeWhile(([, isMouseOverThumbnail]) => isMouseOverThumbnail),
        switchMap(() => {
          this.hovered.emit(true);
          this.isPreviewOverlayOpen = true;
          return combineLatest([
            this.previewOverlayService.createOverlay$(this.elementRef, this.overlayVm, this),
            this.isMouseOverOnThumbnail$,
          ]);
        }),
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        debounceTime(25),
        filter(([isMouseOverPreviewOverlay, isMouseOverOnThumbnail]) => !isMouseOverOnThumbnail && !isMouseOverPreviewOverlay),
        take(1)
      )
      .subscribe(() => {
        this.cleanupInternalState();
        this.previewOverlayService.closeOverlayOfSource(this);
      });
  }

  @HostListener('mouseleave', ['event'])
  public onMouseLeave() {
    if (!this.overlayVm || this.preventHover) {
      return;
    }

    this.isMouseOverOnThumbnail$.next(false);

    const pendingOverlayStopped = this.previewOverlayService.closePendingOverlayOfSource(this);

    if (pendingOverlayStopped) {
      this.hovered.emit(false);
      this.isPreviewOverlayOpen = false;
    }
  }

  @HostListener('click', ['event'])
  public onClick(): void {
    if (!this.overlayVm) {
      return;
    }

    this.previewOverlayService.closeOverlayOfSource(this);
    this.cleanupInternalState();

    if (this.overlayVm.guid) {
      this.itemSelect.emit(this.overlayVm.guid);
    }

    if (!this.playMediaOnClick) {
      return;
    }

    this.playService.playVideo(this.overlayVm.guid, false, this.overlayVm.title[this.currentLanguage]);
  }

  public notifyOverlayClosed() {
    this.cleanupInternalState();
  }

  private cleanupInternalState() {
    this.isPreviewOverlayOpen = false;
    this.hovered.emit(false);
  }

  private hidePreviewOverlay(): boolean {
    if ([Screen.XS, Screen.SM].includes(this.screenService.activeScreen)) {
      return true;
    }
    const boundingRect: DOMRect = (this.elementRef.nativeElement as HTMLElement).getBoundingClientRect();
    const visibilityPercentage = 0.6;
    if (
      !(
        boundingRect.bottom > boundingRect.height * visibilityPercentage &&
        boundingRect.top < window.innerHeight - boundingRect.height * visibilityPercentage
      )
    ) {
      return true;
    }
    return false;
  }

  public ngOnDestroy() {
    super.ngOnDestroy();
    this.overlayVisibleSubscription?.unsubscribe();
    this.isMouseOverOnThumbnail$.complete();
    this.previewOverlayService.closeOverlayOfSource(this);
    this.previewOverlayService.unregisterOverlaySource(this);
  }
}
